import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LinearProgress } from '@mui/material';
import { useHistory } from 'react-router-dom';

import Modal from '../../modules/Modal';
import styles from '../Timeline.module.scss';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { postCreateNewComposer } from '../../../api/composersApi';
import { findSearchValue } from '../../../helpers/getSearchValue';
import { NOTIFICATION, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import { PATHS } from '../../../enums/PATHS';
import { parseQueryString } from '../../../helpers/parseQueryString';
import { buildQueryString } from '../../../helpers/buildQueryString';
import { clearVideoFeeds } from '../../../store/videos/videoSlice';
import { getAllWithoutQueryComposers } from '../../../api';
import { VIDEOFEED } from '../../../enums';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { updateSecondaySelectedClips } from '../../../store/sharedStore/sharedStoreSlice';
import { useHotkeys } from 'react-hotkeys-hook';
import { getTopicsToDisplay } from '../../../helpers/getTopicsToDisplay';
import { TRow } from '../../VideoPage';
import { useClipFavourites } from '../../ClipFavourites/context';

type TNewComposerModal = {
  open: boolean;
  switchModal: () => void;
  skipConfirmation?: boolean;
  setSkipConfirmationModals?: (v: boolean) => void;
  selectedRows?: TRow[];
  videoRows?: TRow[];
  createComposer?: (title: string) => Promise<{ data: VIDEOFEED }>;
};

const NewComposerModal: React.FC<TNewComposerModal> = ({
  open,
  switchModal,
  skipConfirmation,
  setSkipConfirmationModals,
  selectedRows,
  videoRows,
  createComposer,
}) => {
  const [step, setStep] = useState(1);
  const { sortedSelectedClips, markerPosition } = useAppSelector((state) => state.videoStore);
  const { secondarySelectedClips, preserveSecondaryClipOrder } = useAppSelector((state) => state.sharedStore);
  const [title, setTitle] = useState('');
  const toSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const toThirdStepButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const { reload: reloadFavourites } = useClipFavourites();
  const [composerId, setComposerId] = useState('');
  const topicsToDisplay = useMemo(
    () =>
      getTopicsToDisplay({
        sortedSelectedClips,
        secondarySelectedClips,
      }),
    [sortedSelectedClips, secondarySelectedClips],
  );
  const selectedRowIds = selectedRows?.map((row) => String(row.id)) ?? [];
  const selectedRowClipCount = selectedRows?.reduce((sum, row) => sum + (row.clips?.length ?? 0), 0) ?? 0;
  const selectedClipsInRows = sortedSelectedClips.filter((clip) => selectedRowIds.includes(String(clip.topicId))).length;
  const isFullRowSelection =
    selectedRows && selectedRows.length > 0 && selectedRowClipCount > 0 && selectedClipsInRows === selectedRowClipCount;
  const showRowSelectionConfirm = secondarySelectedClips.length === 0 && isFullRowSelection;

  useEffect(() => {
    if (skipConfirmation && open) {
      setStep(2);
    }
  }, [skipConfirmation, open]);

  const closeModal = () => {
    switchModal();
    setLoading(false);
    setStep(1);
    setComposerId('');
    if (!createComposer) dispatch(updateSecondaySelectedClips([]));
    setTitle('');
    if (setSkipConfirmationModals) {
      setSkipConfirmationModals(false);
    }
  };

  const nextStep = () => {
    setStep(2);
  };

  const createWithAllSelected = () => {
    dispatch(updateSecondaySelectedClips([]));
    nextStep();
  };

  const createWithCurrentClip = () => {
    let currentClip = sortedSelectedClips.find((c) => c.rowIndex === markerPosition.rowIndex && c.clipIndex === markerPosition.clipIndex);
    if (!currentClip && videoRows?.[markerPosition.rowIndex]?.clips?.[markerPosition.clipIndex]) {
      const clip = videoRows[markerPosition.rowIndex].clips[markerPosition.clipIndex];
      currentClip = {
        ...clip,
        rowIndex: markerPosition.rowIndex,
        clipIndex: markerPosition.clipIndex,
        topicId: videoRows[markerPosition.rowIndex].id,
        topicName: videoRows[markerPosition.rowIndex].name,
      } as any;
    }
    if (!currentClip) return;
    dispatch(updateSecondaySelectedClips([currentClip]));
    nextStep();
  };

  const onSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e && e.preventDefault();
    const feedId = findSearchValue('feedId') as string;
    try {
      setLoading(true);
      if (createComposer) {
        const res = await createComposer(title);
        setComposerId(res.data.id);
        setStep(3);
        return;
      }
      const selectedClips = secondarySelectedClips.length ? secondarySelectedClips : sortedSelectedClips;
      const clipsInTimelineOrder = preserveSecondaryClipOrder ? selectedClips : selectedClips.slice().sort((a, b) => a.time_start - b.time_start);
      const topicClips = preserveSecondaryClipOrder
        ? clipsInTimelineOrder.slice().sort((a, b) => a.rowIndex! - b.rowIndex!) : clipsInTimelineOrder;
      const topicsIds = topicClips.map((clip) => clip.topicId!);
      const body = {
        title,
        videofeedId: feedId,
        topicsIds: Array.from(new Set(topicsIds)),
        clips: clipsInTimelineOrder.map((clip) => ({ id: clip.id, time_start: clip.time_start, time_end: clip.time_end })),
      };
      const res = await postCreateNewComposer(body);
      reloadFavourites();
      setComposerId(res?.data?.id);
      setStep(3);
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      setLoading(false);
    }
  };

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const onProceed = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const paramsObject = parseQueryString(window.location.search);
      paramsObject.feedId = composerId;
      delete paramsObject.folderId;
      delete paramsObject.parentPage;
      delete paramsObject.pageType;

      const allUserComposers = await getAllWithoutQueryComposers();
      paramsObject.feed = allUserComposers.data.documents.findIndex((composer: VIDEOFEED) => composer.id === paramsObject.feedId);
      history.push(`${PATHS.COMPOSER_PAGE.path}?${buildQueryString(paramsObject)}&pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}`);
      dispatch(clearVideoFeeds());
      closeModal();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useHotkeys('Enter', () => {
    if (step === 2) {
      onSubmit && onSubmit();
    }
  });

  useEffect(() => {
    if (!open) return;

    if (step === 2) {
      const rafId = requestAnimationFrame(() => {
        inputRef.current?.focus();
      });

      return () => cancelAnimationFrame(rafId);
    }
  }, [open, step]);

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    switch (step) {
      case 1:
        toSecondStepButtonRef?.current?.click();
        break;
      case 2:
        toThirdStepButtonRef?.current?.click();
        break;
      case 3:
        submitButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  return (
    <Modal open={open} onClose={closeModal} loading={loading}>
      {step === 1 && (
        <div className={styles.modal}>
          {showRowSelectionConfirm ? (
            <>
              <div className={styles.modalTitle}>Do you want to create a new composer?</div>

              <div className={styles.actionBtns}>
                <ContainedButton onClick={createWithAllSelected}>
                  <div ref={toSecondStepButtonRef}>Create with all {sortedSelectedClips.length} selected clip(s)</div>
                </ContainedButton>
                <ContainedButton onClick={createWithCurrentClip}>Create with current playing clip</ContainedButton>
                <OutlinedButton onClick={closeModal}>Cancel</OutlinedButton>
              </div>
            </>
          ) : (
            <>
              <div className={styles.modalTitle}>Would you like to create a new composer?</div>
              {Object.keys(topicsToDisplay).length > 0 && (
                <div className={styles.clipSummary}>
                  {Object.keys(topicsToDisplay).map((key) => (
                    <div key={key} className={styles.topicRow}>
                      <div className={styles.topicName}>{topicsToDisplay[key].topicName}</div>
                      <div className={styles.clipCount}>
                        {topicsToDisplay[key].clipCount} {topicsToDisplay[key].clipCount === 1 ? 'clip' : 'clips'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.actionBtns}>
                <ContainedButton onClick={nextStep}>
                  <div ref={toSecondStepButtonRef}>Create</div>
                </ContainedButton>
                <OutlinedButton onClick={closeModal}>Cancel</OutlinedButton>
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <form className={styles.modal} onSubmit={onSubmit}>
          <div className={styles.modalTitle}>Please name the composer</div>
          <div>
            <input
              name="title"
              onChange={onTitleChange}
              required
              ref={inputRef}
              value={title}
              className={styles.titleField}
              placeholder={'Enter Composer Name'}
            />
          </div>
          <div className={styles.actionBtns}>
            <ContainedButton type="submit">
              <div ref={toThirdStepButtonRef}>Create</div>
            </ContainedButton>
            <OutlinedButton onClick={closeModal}>Cancel</OutlinedButton>
          </div>
        </form>
      )}
      {step === 3 && (
        <form className={styles.modal} onSubmit={onProceed}>
          <div className={styles.modalTitle}>Composer is created</div>
          <div className={styles.actionBtns}>
            <ContainedButton type="submit">
              <div ref={submitButtonRef}>Proceed to composer</div>
            </ContainedButton>
            <OutlinedButton onClick={closeModal}>Stay here</OutlinedButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default NewComposerModal;
